/**
 * Core entity reads shared by several features (jobs, techs + live state, customers, vehicles, catalog).
 * Features own their workflows and mutations in `features/<x>/provider.ts`; everything that many screens READ lives
 * here so the feature slices don't depend on each other. Live = P1/P3/P5 `/v1/app/*`, fixture = in-memory tenant.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { APP, type Http } from './http';
import type { FixtureDb } from './fixtures/db';
import type { CatalogItem, Customer, Job, Page, Tech, TechLive, Vehicle } from './types';
import { ORDER_STATUSES, phaseOf, type Phase } from './job_status';
import { getTenantTz, localDate, zonedToUtc } from './format';
import {
  apiCatalogItem, apiCustomer, apiJob, apiTech, apiVehicle, deriveTechLive, fetchJob, toList, toPage,
} from './live/dto';
import { useData } from './provider';
import { pollWhenDegraded } from './stream';

export interface JobQuery {
  phase?: Phase | Phase[]; status?: string; tech_id?: string; customer_id?: string; q?: string;
  /** YYYY-MM-DD in tenant tz */ date?: string; location_id?: string; unassigned?: boolean; after?: string; limit?: number; sort?: 'scheduled' | 'created' | 'updated';
}

export interface EntitiesApi {
  jobs(q?: JobQuery): Promise<Page<Job>>;
  job(id: string): Promise<Job>;
  techs(): Promise<Tech[]>;
  techsLive(): Promise<TechLive[]>;
  customers(q?: { q?: string; after?: string; limit?: number }): Promise<Page<Customer>>;
  customer(id: string): Promise<Customer>;
  vehicle(id: string): Promise<Vehicle>;
  vehicles(customerId: string): Promise<Vehicle[]>;
  catalog(): Promise<CatalogItem[]>;
}

const phases = (p?: Phase | Phase[]) => (p == null ? undefined : Array.isArray(p) ? p : [p]);

const enc = encodeURIComponent;
/** The next calendar day of a YYYY-MM-DD string (no tz involved). */
const nextDay = (d: string) => new Date(Date.parse(`${d}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
/** Phase filter → the P1 `status=` list (P1 filters on raw order_status only). */
const statusesFor = (ph?: Phase[]) => (ph ? (ORDER_STATUSES as readonly string[]).filter((s) => ph.includes(phaseOf(s))) : undefined);

/**
 * Live (P1 api/src/modules/{jobs,customers,tenants}). Shapes are mapped by core/live/dto.ts. P1 lists have no
 * `unassigned` / `sort` / phase filters: phase → `status=`, date → tenant-local `from`/`to`, the rest filtered here.
 */
export const liveEntities = (http: Http): EntitiesApi => {
  const api: EntitiesApi = {
    jobs: async (q = {}) => {
      const st = q.status ? [q.status] : statusesFor(phases(q.phase));
      const tz = getTenantTz();
      const query = {
        status: st?.join(','), tech_id: q.tech_id, customer_id: q.customer_id, location_id: q.location_id, q: q.q, after: q.after, limit: q.limit,
        from: q.date ? zonedToUtc(q.date, '00:00', tz) : undefined, to: q.date ? zonedToUtc(nextDay(q.date), '00:00', tz) : undefined,
      };
      const p = toPage(await http.get(`${APP}/jobs`, { query }), apiJob);
      if (q.unassigned) p.items = p.items.filter((j) => !j.assigned_tech_id);
      if (q.sort === 'created') p.items.sort((a, b) => a.created_at.localeCompare(b.created_at));
      if (q.sort === 'updated') p.items.sort((a, b) => b.status_changed_at.localeCompare(a.status_changed_at));
      return p;
    },
    job: (id) => fetchJob(http, id),
    techs: () => http.get(`${APP}/techs`).then((r) => toList(r, apiTech)),
    // State is derived from each tech's active job; positions come from P5's board (`tech.last_location`, stale ones
    // dropped) and later GPS patches arrive over SSE. No board (permission/route) → home positions, as before.
    techsLive: async () => {
      const [techs, jobs, board] = await Promise.all([api.techs(), api.jobs({ phase: ['assigned', 'en_route', 'arrived', 'in_progress'], limit: 200 }),
        http.get<{ techs?: Record<string, any>[] }>('/v1/dispatch/board').catch(() => null)]);
      const pos = new Map((board?.techs ?? []).filter((t) => t.last_location && !t.last_location.stale).map((t) => [String(t.id), t.last_location]));
      return deriveTechLive(techs, jobs.items).map((l) => {
        const p = pos.get(l.tech_id);
        return p ? { ...l, lat: Number(p.lat), lng: Number(p.lng), at: p.recorded_at ?? null } : l;
      });
    },
    customers: (q = {}) => http.get(`${APP}/customers`, { query: q }).then((r) => toPage(r, apiCustomer)),
    customer: (id) => http.get<{ customer: Record<string, any> }>(`${APP}/customers/${enc(id)}`).then((r) => apiCustomer(r.customer)),
    vehicle: (id) => http.get<{ vehicle: Record<string, any> }>(`${APP}/vehicles/${enc(id)}`).then((r) => apiVehicle(r.vehicle)),
    vehicles: (customerId) => http.get(`${APP}/customers/${enc(customerId)}/vehicles`).then((r) => toList(r, apiVehicle, 'vehicles')),
    // The price book is small (hundreds of items): follow keyset pages so pickers and the editor see all of it.
    catalog: async () => {
      const out: CatalogItem[] = [];
      let after: string | undefined;
      for (let i = 0; i < 25; i++) {
        const r = await http.get<{ next?: string | null }>(`${APP}/catalog`, { query: { limit: 200, after } });
        out.push(...toList(r, apiCatalogItem));
        after = r?.next ?? undefined;
        if (!after) break;
      }
      return out;
    },
  };
  return api;
};

/** Keyset pagination over an in-memory array (fixture emulation of `?after=&limit=`). */
export function page<T extends { id: string }>(rows: T[], after?: string, limit = 50): Page<T> {
  const start = after ? rows.findIndex((r) => r.id === after) + 1 : 0;
  const items = rows.slice(start, start + limit);
  return { items, next: start + limit < rows.length ? items[items.length - 1]!.id : null, total: rows.length };
}

export const fixtureEntities = (db: FixtureDb): EntitiesApi => ({
  jobs: (q = {}) => {
    const ph = phases(q.phase);
    const needle = q.q?.toLowerCase();
    let rows = [...db.jobs.values()].filter((j) =>
      (!ph || ph.includes(phaseOf(j.order_status))) && (!q.status || j.order_status === q.status) &&
      (!q.tech_id || j.assigned_tech_id === q.tech_id) && (!q.customer_id || j.customer_id === q.customer_id) &&
      (!q.unassigned || !j.assigned_tech_id) && (!q.location_id || j.location_id === q.location_id) &&
      (!q.date || (!!j.scheduled_start && localDate(j.scheduled_start) === q.date)) &&
      (!needle || [j.job_no, j.customer_name, j.customer_phone_e164, j.service_summary, j.vehicle_label].some((v) => v?.toLowerCase().includes(needle))));
    const key = q.sort === 'created' ? 'created_at' : q.sort === 'updated' ? 'status_changed_at' : 'scheduled_start';
    rows = rows.sort((a, b) => String(a[key] ?? '').localeCompare(String(b[key] ?? '')));
    if (q.sort === 'updated') rows.reverse();
    return db.delay(page(rows, q.after, q.limit));
  },
  job: (id) => { const j = db.jobs.get(id); return j ? db.delay(j) : Promise.reject(db.fail(404, 'not_found')); },
  techs: () => db.delay([...db.techs.values()]),
  techsLive: () => db.delay([...db.live.values()]),
  customers: (q = {}) => {
    const n = q.q?.toLowerCase().replace(/[^\da-z ]/g, '');
    const rows = [...db.customers.values()].filter((c) => !n || c.display_name.toLowerCase().includes(n) || (c.phone_e164 ?? '').includes(n.replace(/\D/g, '') || '~'));
    return db.delay(page(rows.sort((a, b) => a.display_name.localeCompare(b.display_name)), q.after, q.limit));
  },
  customer: (id) => { const c = db.customers.get(id); return c ? db.delay(c) : Promise.reject(db.fail(404, 'not_found')); },
  vehicle: (id) => { const v = db.vehicles.get(id); return v ? db.delay(v) : Promise.reject(db.fail(404, 'not_found')); },
  vehicles: (cid) => db.delay([...db.vehicles.values()].filter((v) => v.customer_id === cid)),
  catalog: () => db.delay(db.catalog),
});

// ─────────────────────────────────────────── hooks (query keys are the cache contract; see core/realtime.ts)
export const qk = {
  jobs: (q?: JobQuery) => (q ? ['jobs', 'list', q] as const : ['jobs', 'list'] as const),
  job: (id: string) => ['jobs', 'one', id] as const,
  techs: ['techs', 'list'] as const,
  techsLive: ['techs', 'live'] as const,
  customers: (q?: object) => (q ? ['customers', 'list', q] as const : ['customers', 'list'] as const),
  customer: (id: string) => ['customers', 'one', id] as const,
  vehicle: (id: string) => ['vehicles', 'one', id] as const,
  vehicles: (cid: string) => ['vehicles', 'of', cid] as const,
  catalog: ['catalog'] as const,
};

export function useJobs(q: JobQuery = {}) {
  const { data } = useData();
  return useQuery({ queryKey: qk.jobs(q), queryFn: () => data.entities.jobs(q), placeholderData: keepPreviousData, refetchInterval: pollWhenDegraded });
}
export function useJob(id: string | null | undefined) {
  const { data } = useData();
  return useQuery({ queryKey: qk.job(id ?? ''), queryFn: () => data.entities.job(id!), enabled: !!id, refetchInterval: pollWhenDegraded });
}
export function useTechs() {
  const { data } = useData();
  return useQuery({ queryKey: qk.techs, queryFn: () => data.entities.techs(), staleTime: 60_000 });
}
export function useTechsLive() {
  const { data } = useData();
  return useQuery({ queryKey: qk.techsLive, queryFn: () => data.entities.techsLive(), refetchInterval: pollWhenDegraded });
}
export function useCustomers(q: { q?: string; after?: string; limit?: number } = {}) {
  const { data } = useData();
  return useQuery({ queryKey: qk.customers(q), queryFn: () => data.entities.customers(q), placeholderData: keepPreviousData });
}
export function useCustomer(id: string | null | undefined) {
  const { data } = useData();
  return useQuery({ queryKey: qk.customer(id ?? ''), queryFn: () => data.entities.customer(id!), enabled: !!id });
}
export function useVehicle(id: string | null | undefined) {
  const { data } = useData();
  return useQuery({ queryKey: qk.vehicle(id ?? ''), queryFn: () => data.entities.vehicle(id!), enabled: !!id });
}
export function useVehicles(customerId: string | null | undefined) {
  const { data } = useData();
  return useQuery({ queryKey: qk.vehicles(customerId ?? ''), queryFn: () => data.entities.vehicles(customerId!), enabled: !!customerId });
}
export function useCatalog() {
  const { data } = useData();
  return useQuery({ queryKey: qk.catalog, queryFn: () => data.entities.catalog(), staleTime: 5 * 60_000 });
}
/** Tech lookup by id (for avatars/names everywhere). */
export function useTechMap() {
  const q = useTechs();
  const map = new Map((q.data ?? []).map((t) => [t.id, t]));
  return { map, isLoading: q.isLoading };
}
